import type { QueryClient, QueryKey } from '@tanstack/react-query';

const toQueryKeys = (keys: QueryKey | QueryKey[] | undefined): QueryKey[] => {
  if (!keys) return [];
  return Array.isArray(keys[0]) ? (keys as QueryKey[]) : [keys as QueryKey];
};

// Invalidates the keys a consumer declared via `mutationKey` (see the
// Invalidation section in `src/hooks/AGENTS.md`). Accepts either a single
// `QueryKey` or an array of `QueryKey`s — detected by whether the first
// element is itself an array.
export const invalidateMutationKey = (
  queryClient: QueryClient,
  mutationKey: QueryKey | QueryKey[] | undefined
): void => {
  toQueryKeys(mutationKey).forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
};

export const resetQueryKeys = (
  queryClient: QueryClient,
  resetKey: QueryKey | QueryKey[] | undefined
): void => {
  toQueryKeys(resetKey).forEach((queryKey) => queryClient.resetQueries({ queryKey }));
};

export const stableFilterValues = (filters?: Record<string, unknown>): readonly string[] => {
  if (!filters) return [];
  return Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${Array.isArray(v) ? v.join(',') : String(v)}`);
};

export const joinInclude = (include?: string[]): string | undefined =>
  include && include.length > 0 ? include.join(',') : undefined;

// Settles a `mutateAsync` promise so it never rejects: resolves to the data on
// success, or `undefined` on failure (the mutation's `onError` already surfaced
// the toast). This is what lets consumers `await` a mutation — or fire it and
// ignore the promise — without a per-call `try/catch`; success side effects go
// in `onSuccess`, error side effects in `onError`. See `useSwallowedMutation`
// and the mutation pattern in `src/hooks/AGENTS.md`.
export const swallowMutation = async <T>(promise: Promise<T>): Promise<T | undefined> => {
  try {
    return await promise;
  } catch {
    return undefined;
  }
};

// React Query `retry` predicate that never retries client errors (4xx): they
// won't succeed on retry and would keep a list spinner spinning while the
// consumer never sees the error (e.g. a 404 on an out-of-range page after the
// effective hotel chain changes). Server errors (5xx) and network failures keep
// the default 3 retries. Applied per list query; see `src/hooks/AGENTS.md` on
// why this is not centralised in the shared QueryClient yet.
export const retryServerErrorsOnly = (failureCount: number, error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status ?? 0;

  // 401 gets exactly one retry: the axios interceptor refreshes the session
  // between React Query attempts, so a request caught in a token-rotation window
  // succeeds on the next try. A second 401 means the session is genuinely dead
  // (the interceptor has already logged it out) — stop there.
  if (status === 401) {
    return failureCount < 1;
  }

  if (status >= 400 && status < 500) {
    return false;
  }

  return failureCount < 3;
};

// `responseType: 'arraybuffer'` buffers error bodies too, hiding the JSON:API `errors` payload from
// `handleRequestError`. Decode it back in place so a failed export reports what the backend said.
export const decodeBufferedErrorBody = (error: unknown): unknown => {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (!(response?.data instanceof ArrayBuffer)) return error;

  try {
    response.data = JSON.parse(new TextDecoder().decode(response.data));
  } catch {
    // Not JSON (an HTML gateway page, an empty body) — leave the buffer as it came.
  }

  return error;
};

// Coerces a filter value into the comma-joined string format JSON:API expects.
// Empty strings, empty arrays, null and undefined all become `undefined` so
// they're omitted from the request payload entirely.
export const joinCsv = (value?: string | readonly (string | number)[]): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value.length ? value.join(',') : undefined;
  return value === '' ? undefined : String(value);
};

/**
 * Coerces a multipart file field to what the generated client actually needs.
 *
 * `undefined` omits the field, a `File` uploads it, and `null` clears it — but the client appends
 * whatever it is handed whenever the value is not `undefined` (`thumbnail !== undefined &&
 * form.append(...)`), so a literal `null` would upload the string `"null"`; the empty field is the
 * clear the backend reads, and the one the legacy screens sent. The SDK types these fields
 * `File | null`, so the empty value goes through this single cast — the same contained-boundary
 * contract as `clearToOne` in `_relationships.ts`. Delete it if the SDK ever types the clear.
 */
export const toMultipartFile = (file?: File | null): File | undefined =>
  (file === null ? '' : file) as File | undefined;
